<template>
  <div class="l-learn-audio-sort">
    <!-- Loading overlay -->
    <div class="loading-overlay" v-if="loading">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>

    <!-- Audio player control bar -->
    <div class="audio-control-bar" v-if="!loading">
      <div class="back-button" @click="goBack">
        <img src="./images/back.png" alt="Back" />
      </div>
      <audio-player 
        :audio-src="currentAudioItem ? currentAudioItem.audioSrc : ''"
        :audio-title="currentAudioItem ? currentAudioItem.name : ''"
        :can-play="canPlayAudio"
        @play="handlePlay"
        @pause="handlePause"
        @replay="handleReplay"
        @audio-ended="handleAudioEnded"
        class="audio-player"
      />
      <div class="countdown-timer">
        <img src="./images/clock.png" alt="Timer" class="timer-icon" />
        <span class="timer-text">倒计时: {{ countdown }} 秒</span>
      </div>
    </div>

    <!-- Image grid for sorting -->
    <div class="image-grid" v-if="!loading">
      <div class="image-grid-content">
        <ImageItem
          v-for="(image, index) in shuffledImages" 
          :key="image.id" 
          :src="image.src"
          :index="index"
          :canPickImages="canPickImages"
          :isRevealed="imagesRevealed"
          @select-image="selectImage"
        />
      </div>
    </div>

    <!-- Selection Modal Component -->
    <SelectionModal
      :show="showSelectionModal"
      :selectedImages="selectedImagesInOrder"
      :isComplete="isSelectionComplete"
      @close="closeSelectionModal"
      @submit="submitSelections"
    />

    <!-- 预加载音频 -->
    <audio ref="correctAudio" src="@/assets/audio/correct.mp3" preload="auto" style="display: none;"></audio>
    <audio ref="errorAudio" src="@/assets/audio/error.mp3" preload="auto" style="display: none;"></audio>
  </div>
</template>

<script>
import elementTypes from '../../minxin/elementTypes'
import { createNamespacedHelpers } from 'vuex'
import UPModel from '@/models/imgup'
import AudioPlayer from './components/AudioPlayer'
import ImageItem from './components/ImageItem'
import SelectionModal from './components/SelectionModal'
// 音效
// import correctAudio from '@/assets/audio/correct.mp3'
// import errorAudio from '@/assets/audio/error.mp3'

const upModel = new UPModel()
const { mapActions, mapGetters } = createNamespacedHelpers('course')

export default {
  name: 'LLearnAudioSort',
  mixins: [elementTypes],
  components: {
    AudioPlayer,
    ImageItem,
    SelectionModal,
  },
  data() {
    return {
      loading: true,
      countdownStorageKey: 'audioSortCountdown',
      countdown: 2000, // Countdown in seconds
      countdownInterval: null,
      audioItems: [], // Will store the audio data from element.datas
      imageItems: [], // Will store the image data from element.datas
      shuffledImages: [], // Randomized images for display
      currentAudioIndex: 0, // Current audio being played
      currentPlayedAudioIndex: -1, // Last audio that was played
      selectedImages: [], // Images that have been selected
      showSelectionModal: false, // Controls the selection modal visibility
      canPlayAudio: true, // Controls if audio can be played
      canPickImages: false, // Controls if images can be picked
      feedbackAudio: null, // Audio element for feedback sounds
      imagesRevealed: false, // Controls if images are revealed (flipped)
      hasAudioPlayedOnce: false, // Tracks if audio has been played at least once
    };
  },
  computed: {
    ...mapGetters(['element']),
    
    // Get the current audio item being played
    currentAudioItem() {
      return this.audioItems[this.currentAudioIndex] || null;
    },
    
    // Get selected images in their original order
    selectedImagesInOrder() {
      // Just return a copy of the selected images
      // They're already in the order they were added
      return [...this.selectedImages];
    },
    
    // Check if all images have been selected
    isSelectionComplete() {
      return (this.currentAudioIndex === this.audioItems.length - 1) && (this.shuffledImages.length === 0)
    },
  },
  watch: {
    element: {
      immediate: true,
      handler(val) {
        if (!val) return;
        this.loading = true;
        this.processElementData(val);
      }
    },
    'element.id': {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;
        
        this.countdownStorageKey = `audioSortCountdown-${newVal}`;
        this.getCountdown();
        this.startCountdown();
      }
    }
  },
  methods: {
    ...mapActions(['STUDY_RESULT']),
    
    getCountdown() {
      const countdown = localStorage.getItem(this.countdownStorageKey);
      this.countdown = countdown || this.element?.attrs?.duration || 2000;
    },
    // Process the element data to extract audio and image information
    processElementData(elementData) {
      if (!elementData || !elementData.datas || !Array.isArray(elementData.datas)) {
        console.error('Invalid element data structure');
        this.loading = false;
        return;
      }
      
      // 1. Extract all resource paths for loading
      const allAudioPaths = [];
      const allImagePaths = [];
      
      elementData.datas.forEach(item => {
        if (item.attrs && item.attrs.audio) {
          allAudioPaths.push(item.attrs.audio);
        }
        if (item.attrs && item.attrs.picture) {
          allImagePaths.push(item.attrs.picture);
        }
      });
      
      // 2. Load all resources first
      this.loadResources([...allAudioPaths, ...allImagePaths]).then(resources => {
        // 3. Process all items with their resources
        const allItems = elementData.datas.map((item, index) => {
          return {
            id: item.id,
            name: item.name,
            audioSrc: resources[allAudioPaths[index]] || '',
            imageSrc: resources[allImagePaths[index]] || '',
            sort: item.attrs.sort,
            selected: false,
          };
        });
        
        // 4. Process previously answered items (from records)
        this.processAnsweredItems(allItems, elementData.records);
        
        // 5. Filter out answered items for audio playback
        const unansweredItems = this.filterAnsweredItems(allItems, elementData.records);
        
        // 6. Set audio items for playback (only unanswered)
        this.audioItems = unansweredItems.map(item => ({
          id: item.id,
          name: item.name,
          audioSrc: item.audioSrc,
          sort: item.sort,
        }));
        
        // 7. Create image items for display (all unanswered items)
        this.imageItems = unansweredItems.map(item => ({
          id: item.id,
          name: item.name,
          src: item.imageSrc,
          sort: item.sort,
          selected: item.selected,
        }));
        
        // 8. Create shuffled copy of images for display
        this.shuffledImages = this.shuffleArray([...this.imageItems]);
        
        // 9. Initialize first audio
        this.initAudio();
        
        // 10. 检查是否所有题目都已作答完成
        if (this.audioItems.length === 0 && this.imageItems.length === 0) {
          // 所有题目都已作答，直接提交
          this.loading = false;
          this.$nextTick(() => {
            this.$emit('submit');
          });
          return;
        }
        
        this.loading = false;
      }).catch(error => {
        console.error('Error loading resources:', error);
        this.loading = false;
      });
    },
    
    // Process already answered items and add them to selectedImages
    processAnsweredItems(allItems, records) {
      if (!records || !Array.isArray(records) || records.length === 0) {
        return; // No records to process
      }
      
      // Map records to their corresponding items
      records.forEach(record => {
        const matchedItem = allItems.find(item => item.id === record.eleId);
        if (matchedItem) {
          // Mark the item as selected
          matchedItem.selected = true;
          
          // Add to selectedImages
          this.selectedImages.push({
            id: matchedItem.id,
            name: matchedItem.name,
            src: matchedItem.imageSrc,
            sort: matchedItem.sort,
            audioName: matchedItem.name,
            isIncorrect: !record.right, // Mark as incorrect if not right
          });
        }
      });
    },
    
    // Filter out items that have already been answered
    filterAnsweredItems(items, records) {
      if (!records || !Array.isArray(records) || records.length === 0) {
        return items; // Return all items if no records
      }
      
      // Create a Set of answered item IDs for quick lookup
      const answeredIds = new Set(records.map(record => record.eleId));
      
      // Filter out items that have already been answered
      return items.filter(item => !answeredIds.has(item.id));
    },
    
    // Load resources (audio and images) using upModel
    loadResources(paths) {
      return new Promise((resolve, reject) => {
        if (!paths.length) {
          resolve({});
          return;
        }
        
        upModel.resource({ paths })
          .then((res) => {
            // Create a map of original path to full URL
            const resourceMap = {};
            if (res && res.data && Array.isArray(res.data)) {
              paths.forEach((path, index) => {
                resourceMap[path] = res.data[index];
              });
            }
            resolve(resourceMap);
          })
          .catch((err) => {
            console.error('Error loading resources:', err);
            reject(err);
          });
      });
    },
    
    // Shuffle array (Fisher-Yates algorithm)
    shuffleArray(array) {
      const newArray = [...array];
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
      }
      return newArray;
    },
    
    // Initialize audio for the current index - just store the source now
    initAudio() {
      // No direct audio initialization needed - handled by AudioPlayer component
    },
    
    // Audio player event handlers
    handlePlay() {
      // When audio starts playing, images should not be pickable
      this.canPickImages = false;
    },

    handlePause() {
      // No change to pickability when paused
    },

    handleReplay() {
      // When audio is replayed, reset pickability but keep images revealed
      this.canPickImages = false;
    },

    handleAudioEnded() {
      this.currentPlayedAudioIndex = this.currentAudioIndex;

      // Enable picking images when audio ends
      this.canPickImages = true;

      // Always allow replay of current audio
      this.canPlayAudio = true;

      // 首次播放完成后，翻转显示图片
      if (!this.hasAudioPlayedOnce) {
        this.hasAudioPlayedOnce = true;
        this.imagesRevealed = true;
      }
    },
    
    // Format time display (mm:ss)
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    
    // Start countdown timer
    startCountdown() {
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
          localStorage.setItem(this.countdownStorageKey, this.countdown);
        } else {
          this.stopCountdown();
          localStorage.removeItem(this.countdownStorageKey);
          this.$emit('submit')
        }
      }, 1000);
    },
    
    // Stop countdown timer
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    
    // Play feedback sound based on correctness
    playFeedbackSound(isCorrect) {
      // 停止之前的反馈音频
      if (this.feedbackAudio) {
        this.feedbackAudio.pause();
        this.feedbackAudio.currentTime = 0;
      }

      // 使用预加载的音频元素
      this.feedbackAudio = isCorrect ? this.$refs.correctAudio : this.$refs.errorAudio;
      
      if (this.feedbackAudio) {
        // 重置播放位置
        this.feedbackAudio.currentTime = 0;
        
        // 播放音频
        this.feedbackAudio.play().catch(error => {
          console.error('播放提示音出错:', error);
        });
      }
    },
    
    // Select an image
    selectImage(index) {
      // If images are not pickable, do nothing
      if (!this.canPickImages) return;
      
      const selectedImage = this.shuffledImages[index];
      
      // Get the current audio item
      const currentAudio = this.audioItems[this.currentAudioIndex];
      if (!currentAudio) return;
      
      // Find the correct image for the current audio
      // We need to match the current audio's ID with the image's ID
      const correctImageIndex = this.shuffledImages.findIndex(img => img.id === currentAudio.id);
      
      // Get the correct image if it exists
      let correctImage = null;
      let isIncorrect = false;
      
      if (correctImageIndex !== -1) {
        correctImage = this.shuffledImages[correctImageIndex];
        // Mark if the selection was incorrect
        isIncorrect = selectedImage.id !== correctImage.id;
        
        // Play appropriate feedback sound
        this.playFeedbackSound(!isIncorrect);
        
        // Add the CORRECT image to selectedImages with a flag if the selection was incorrect
        this.selectedImages.push({
          id: correctImage.id,
          name: correctImage.name,
          src: correctImage.src,
          sort: correctImage.sort,
          audioName: currentAudio.name,
          isIncorrect: isIncorrect
        });
        
        // Remove the CORRECT image from shuffledImages
        this.shuffledImages.splice(correctImageIndex, 1);

        // 重新打乱剩余图片的顺序
        this.shuffledImages = this.shuffleArray([...this.shuffledImages]);

        // Submit the result for this question
        this.STUDY_RESULT({
          elementId: this.element.id,
          postData: {
            eleId: correctImage.id,
            sort: correctImage.sort,
            right: !isIncorrect,
            suggestedScore: !isIncorrect ? 100 : 0,
            leftTime: this.countdown,
          },
        });
      } else {
        // If we couldn't find the correct image, this is an error state
        console.error('Could not find correct image for current audio:', currentAudio);
        // Still play error sound for feedback
        this.playFeedbackSound(false);
        return;
      }
      
      // Update played audio index and advance to next audio if available
      this.currentPlayedAudioIndex = this.currentAudioIndex;

      // If there are more audio items, advance to the next one
      if (this.currentAudioIndex < this.audioItems.length - 1) {
        this.currentAudioIndex++;
      }

      // Disable picking until next audio plays
      this.canPickImages = false;
      // 重置翻转状态，下一题的图片重新盖住
      this.imagesRevealed = false;
      this.hasAudioPlayedOnce = false;
      this.showSelectionModal = true;
    },
    
    // Close the selection modal
    closeSelectionModal() {
      this.showSelectionModal = false;

      // 确保图片重置为盖住状态
      this.imagesRevealed = false;
      this.hasAudioPlayedOnce = false;

      // Auto-play the next audio if available
      if (this.currentAudioIndex < this.audioItems.length) {
        // Trigger audio playback via AudioPlayer component
        this.$nextTick(() => {
          // Find the audio player component and trigger play
          const audioPlayerRef = this.$children.find(child => child.$options.name === 'AudioPlayer');
          if (audioPlayerRef) {
            audioPlayerRef.playAudio();
          }
        });
      }
    },
    
    submitSelections() {
      // If selection is not complete, just close the modal and continue
      if (!this.isSelectionComplete) {
        this.closeSelectionModal();
        return;
      }
      this.stopCountdown();
      localStorage.removeItem(this.countdownStorageKey);
      this.$emit('submit');
    },
    
    goBack() {
      this.$emit('back');
    }
  },
  beforeDestroy() {
    // No need to clean up audio directly anymore
    this.stopCountdown();
  },
};
</script>

<style scoped>
.l-learn-audio-sort {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  background-color: #CCCFFF;
  padding: 20px;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Loading overlay styles */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(204, 207, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #8bc34a;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 18px;
  color: #333;
  font-weight: bold;
}

/* Audio control bar styles */
.audio-control-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  position: relative;
}

.back-button {
  cursor: pointer;
}

.back-button img {
  width: 56px;
  height: 56px;
}

.countdown-timer {
  display: flex;
  align-items: center;
}

.timer-icon {
  width: 48px;
  height: 48px;
  margin-right: 5px;
}

.timer-text {
  font-size: 18px;
  color: #333;
}

/* Image grid styles */
.image-grid {
  flex: 1;
  margin-top: 20px;
  overflow-y: auto;
}
.image-grid-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  min-height: 100%;
  margin-left: -1%;
}
.image-grid::-webkit-scrollbar {
  display: none;
}
</style>